'use client';
import { nanoid } from 'nanoid';
import { Dispatch, FormEvent, SetStateAction, useCallback, useState } from 'react';

import { confirmWithDialog } from '@/components/confirm-dialog';
import { closeHighestDrawer, Drawer } from '@/components/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { Pet } from '@/db/queries/pets';
import { User } from '@/db/queries/users';
import { PetState, Role } from '@/db/schema';
import { gtagEvent } from '@/utils/gtag';

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { deletePet, savePet } from './patient-actions';

const DEFAULT_PET: Pet = {
  id: nanoid(),
  userId: '',
  workflowId: null,
  breed: null,
  name: null,
  pounds: null,
  species: null,
  state: PetState.Pending,
  paidAt: null,
  evaluatedAt: null,
  evaluatedBy: null,
  updated: new Date().toISOString(),
  created: new Date().toISOString(),
};

type PetEditButtonProps = {
  children?: React.ReactNode;
  doNotReload?: boolean;
  onSubmitted?: () => void;
  pet?: Pet;
  track?: boolean;
};

export function PetEditButton({
  children,
  doNotReload = false,
  onSubmitted,
  pet = DEFAULT_PET,
  track = false,
}: PetEditButtonProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(pet.name || '');
  const [species, setSpecies] = useState(pet.species || '');
  const [breed, setBreed] = useState(pet.breed || '');
  const [pounds, setPounds] = useState(pet.pounds || '');
  const isValid = name && species && breed && pounds;
  const disabled = submitting || !isValid;

  return (
    <Drawer
      actions={
        <div className='flex flex-row-reverse gap-4'>
          <Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const submitButton = document.getElementById('pet-edit-submit') as HTMLButtonElement | null;

              submitButton?.click();
            }}
          >
            Save
          </Button>
          <Button disabled={submitting} variant='ghost'>
            Cancel
          </Button>
        </div>
      }
      onClose={() => {
        const autofocusInput = document.querySelector('input[autofocus]') as HTMLInputElement | null;

        autofocusInput?.focus();
      }}
      onOpen={() => track && gtagEvent('add_pet')}
      scrim
      title='Edit pet'
      trigger={
        children || (
          <Button className='hover:bg-neutral-200' variant='ghost'>
            Edit
          </Button>
        )
      }
    >
      <form
        className='flex flex-col gap-4'
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitting(true);

          try {
            const isDefault = pet.id === DEFAULT_PET.id;

            await savePet({ id: isDefault ? nanoid() : pet.id, breed, name, pounds: +pounds, species });

            setName('');
            setBreed('');
            setPounds('');
            setSpecies('');

            track && (await gtagEvent('save_pet'));

            !doNotReload && location.reload();
          } catch (e) {
            const error = e as Error;

            toast({ title: 'Failed to save pet', description: error.message });
            console.error(e);
          }

          setSubmitting(false);

          onSubmitted?.();
        }}
      >
        <div className='w-full items-center gap-1.5'>
          <Label>Name</Label>
          <Input
            autoFocus
            id='name'
            onChange={(e) => setName(e.target.value)}
            placeholder='Rex...'
            required
            type='text'
            value={name}
          />
        </div>

        <div className='w-full items-center gap-1.5'>
          <Label>Species</Label>
          <Select required onValueChange={(answer) => setSpecies(answer)} value={species}>
            <SelectTrigger>
              <SelectValue placeholder='Species...' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Species</SelectLabel>
                <SelectItem value='dog'>Dog</SelectItem>
                <SelectItem value='cat'>Cat</SelectItem>
                <SelectItem value='other'>Other</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className='w-full items-center gap-1.5'>
          <Label>Breed</Label>
          <Input
            id='breed'
            onChange={(e) => setBreed(e.target.value.toLowerCase())}
            placeholder='labrador...'
            required
            type='text'
            value={breed}
          />
        </div>

        <div className='w-full items-center gap-1.5'>
          <Label>Pounds</Label>
          <Input
            id='pounds'
            onChange={(e) => {
              const value = e.target.value;

              if (!value) {
                setPounds('');
              } else {
                const normalizedValue = Math.min(299, Math.max(1, parseInt(value, 10)));

                setPounds(normalizedValue);
              }
            }}
            placeholder=''
            type='number'
            required
            value={pounds}
          />
        </div>

        <button className='hidden' disabled={disabled} id='pet-edit-submit' type='submit'>
          Submit
        </button>
      </form>
      {pet.userId && (
        <div className='text-right w-full py-4'>
          <Button
            onClick={(e) => {
              e.preventDefault();

              confirmWithDialog({
                title: 'Delete Pet',
                message: `Are you sure you want to delete ${name}?`,
                onConfirm: async () => {
                  await deletePet(pet.id);

                  location.reload();
                },
              });
            }}
            variant='destructive'
          >
            Delete Pet
          </Button>
        </div>
      )}
    </Drawer>
  );
}
